import { IconButton, Link, Typography } from "@mui/material";
import Unarchive from "@mui/icons-material/Unarchive";
import { useState } from "react";
import { api } from "../../api/API";
import { Goal } from "../../api/Projects";
import { useSnackAction } from "../../misc/SnackAction";
import { PanelAdd, PanelAddField, panelFontSize, PanelRow, PanelSection } from "../task/panel/PanelSection";

// ProjectGoals breaks a project into the pieces its tasks work towards. A goal is
// never completed, only archived, which happens from the goal's own panel.
export function ProjectGoals(props: {
    projectId: number;
    goals: Goal[];
    onOpenGoal: (goal: Goal) => void;
}) {
    const [showArchived, setShowArchived] = useState(false);

    const active = props.goals.filter(goal => !goal.archived);
    const archived = props.goals.filter(goal => goal.archived);

    const add = useSnackAction(async (input: { name: string }) => {
        await api.projects.upsertGoal({ project: props.projectId, name: input.name });
    }, { success: "Goal added" });

    // restoring puts back the tasks the archive took, and nothing else
    const restore = useSnackAction(async (input: { goal: Goal }) => {
        await api.projects.upsertGoal({ ...input.goal, archived: false });
    }, { success: "Goal restored" });

    const busy = restore.loading;

    return (
        <PanelSection label="Goals">
            {active.map(goal => (
                <GoalRow key={goal.id} goal={goal} onOpen={props.onOpenGoal} />
            ))}

            {active.length === 0 && (
                <PanelRow>
                    <Typography variant="caption" color="text.secondary">
                        Split the project into goals, such as phases or features. Then put each
                        task in a goal from the task's Project field.
                    </Typography>
                </PanelRow>
            )}

            {showArchived && archived.map(goal => (
                <GoalRow key={goal.id} goal={goal} onOpen={props.onOpenGoal}>
                    <IconButton
                        size="small"
                        title="Restore"
                        disabled={busy}
                        onClick={() => restore.callback({ goal: goal })}
                    >
                        <Unarchive fontSize="inherit" />
                    </IconButton>
                </GoalRow>
            ))}

            <PanelAdd>
                <PanelAddField
                    placeholder="Add a goal to this project"
                    onAdd={name => add.callback({ name: name })}
                />

                {archived.length > 0 && (
                    <Link
                        component="button"
                        type="button"
                        variant="caption"
                        underline="hover"
                        color="text.secondary"
                        onClick={() => setShowArchived(current => !current)}
                    >
                        {showArchived
                            ? "Hide archived"
                            : "Show " + archived.length + " archived"}
                    </Link>
                )}
            </PanelAdd>
        </PanelSection>
    );
}

function GoalRow(props: {
    goal: Goal;
    onOpen: (goal: Goal) => void;
    children?: React.ReactNode;
}) {
    const goal = props.goal;

    return (
        <PanelRow>
            <Typography
                variant="body2"
                noWrap
                onClick={() => props.onOpen(goal)}
                sx={{
                    flex: 1, minWidth: 0, cursor: "pointer", fontSize: panelFontSize,
                    color: goal.archived ? "text.secondary" : undefined,
                    "&:hover": { textDecoration: "underline" },
                }}
            >
                {goal.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
                {goal.archived ? "Archived" : goal.openCount + " open"}
            </Typography>
            {props.children}
        </PanelRow>
    );
}
